import { Inline } from '@bedrock-layout/primitives'
import {
  Heading,
  ArticleCard,
  FullWidthTextSection,
  FilterReactAria,
  Paragraph,
  BrandButton,
} from '@okam/brand-ui/index'
import { extractRelativePath } from '@okam/html2react/index'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { i18n, useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'
import type { Key } from 'react'
import { Section, Item } from 'react-stately'
import tw from 'twin.macro'
import { MainLayout } from '@/components/layout'
import { QueryKeys } from '@/lib/react-query'
import usePosts from '@/lib/react-query/usePosts'
import { useFragment } from 'gql'
import type {
  NouvellesQuery,
  SiteOptionsQuery,
  AllPostsQuery,
  MainMenuFragment,
  AllNewsCategoriesQuery,
} from 'gql/graphql'
import { allMembersFragment } from 'query/fragments'

export interface TNouvellesPageProps {
  pageProps: NouvellesQuery & SiteOptionsQuery & AllPostsQuery & AllNewsCategoriesQuery
}

const NouvellesPage = (props: TNouvellesPageProps) => {
  const { pageProps } = props
  const { page, siteOptions, mainMenu, categories } = pageProps
  const allMembers = useFragment(allMembersFragment, pageProps.members)
  const categoriesData = categories?.nodes ?? []

  const [selectedValue, setSelectedValue] = useState<string>('')
  const [lastPostId, setLastPostId] = useState<string>('')
  const [hasNextPage, setHasNextPage] = useState<boolean>(true)
  const [queryLastPostId, setQueryLastPostId] = useState<string>(lastPostId)

  const { t } = useTranslation('common')
  const queryClient = useQueryClient()
  const router = useRouter()

  const {
    data: fetchedData,
    isSuccess,
    isLoading,
    isFetching,
    refetch,
    fetchNextPage,
  } = usePosts(QueryKeys.GetPosts, selectedValue, i18n?.language ?? 'fr', queryLastPostId, hasNextPage)

  const filterAriaLabel = t('ARIA.FILTER')
  const filterPlaceholder = t('FILTER.BY')
  const filterLabel = t('FILTER.NEWS')
  const filterTypesLabel = t('FILTER.SELECT_FIRST_SECTION_LABEL_NOUVELLES')
  const loadingMsg = t('COMMON.LOADING')
  const noResult = t('FILTER.NO_RESULT')

  const resetQueryParams = () => {
    setLastPostId('')
    setHasNextPage(true)
    setQueryLastPostId('')
  }

  useEffect(() => {
    if (fetchedData && !isFetching) {
      const lastPosts = fetchedData.pages.slice(-1)[0]
      const lastPageInfo = lastPosts?.posts?.pageInfo
      setLastPostId(lastPageInfo?.endCursor ?? lastPostId)
      setHasNextPage(lastPageInfo?.hasNextPage ?? hasNextPage)
    }

    router.events.on('beforeHistoryChange', () => {
      resetQueryParams()
      queryClient.clear()
    })
  }, [fetchedData, lastPostId, hasNextPage, isFetching, router.events, queryClient])

  const handleSelect = (value: Key) => {
    resetQueryParams()

    if (value === 'all') {
      setSelectedValue('')
    }
    if (typeof value === 'string' && value !== 'all') {
      setSelectedValue(value)
    }

    queryClient.clear()
    refetch()
  }

  const handleLoadMore = () => {
    setQueryLastPostId(lastPostId)

    const fetchNextTimeout = setTimeout(() => {
      fetchNextPage()
      clearTimeout(fetchNextTimeout)
    }, 10)
  }

  return (
    <MainLayout
      siteOptionsProps={siteOptions}
      seo={page?.seo}
      allMembers={allMembers}
      menuItems={mainMenu as MainMenuFragment}
      variant="colored"
      translatedUri={page?.translatedUri}
    >
      <FullWidthTextSection
        tw="pt-40 xl:pt-[12.5rem]"
        minHeight="400px"
        xAlign="left"
        yAlign="bottom"
        patternPosition="right"
        colorTheme="white"
      >
        <Heading as="h1" variant="h1" tw="uppercase text-[theme('colors.tertiary[5]')] w-1/2 ">
          {page?.title}
        </Heading>
      </FullWidthTextSection>

      <Inline
        justify="start"
        tw="relative flex flex-col h-full pb-12 px-6 mx-auto md:px-10 xl:px-14 xl:pb-32 w-full max-w-[1920px]"
      >
        <div tw="flex flex-col gap-6 items-start sm:items-end sm:gap-y-0 sm:flex-row justify-between mb-8 w-full">
          <FilterReactAria
            id="news-filter"
            label={filterLabel}
            name="news-filter"
            placeholderLabel={filterPlaceholder}
            ariaLabel={filterAriaLabel}
            onSelectionChange={(value: Key) => handleSelect(value)}
            selectedKey={selectedValue}
            disabled={isLoading}
          >
            <Section title={filterTypesLabel}>
              {categoriesData?.map((cat) => (
                <Item textValue={cat.name ?? ''} key={cat.slug}>
                  <Paragraph variant="p" as="p">
                    {cat.name}
                  </Paragraph>
                </Item>
              ))}
            </Section>
            <Item textValue="all" key="all">
              <Paragraph variant="p" as="p">
                {t('FILTER.SELECT_ALL')}
              </Paragraph>
            </Item>
          </FilterReactAria>
        </div>

        <div tw="relative w-full">
          {(isLoading || isFetching) && (
            <div tw="sticky top-1/4 inset-x-0 w-full text-center">
              <div tw="absolute top-0 inset-x-0 m-auto border-4 border-primary-1 border-b-transparent rounded-full w-12 h-12 animate-spin" />
              <Paragraph as="p" variant="p" tw="absolute inset-x-0 top-12 pt-4">
                {loadingMsg}
              </Paragraph>
            </div>
          )}

          <div css={[tw`transition-all `, isLoading || isFetching ? tw`opacity-10 pointer-events-none` : '']}>
            {isSuccess && !fetchedData && (
              <div tw="relative">
                <Paragraph as="p" variant="p" tw="pt-4">
                  {noResult}
                </Paragraph>
              </div>
            )}

            {isSuccess && fetchedData && (
              <div tw="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                {fetchedData?.pages?.map((fetchedPage) =>
                  fetchedPage?.posts?.nodes.map((fetchedPost) => {
                    const uri = extractRelativePath(fetchedPost.uri ?? '')
                    return (
                      <ArticleCard
                        key={fetchedPost.id}
                        label={fetchedPost.categories?.nodes[0]?.name ?? ''}
                        date={fetchedPost.date ?? ''}
                        permalink={uri}
                        title={fetchedPost.title ?? ''}
                        ariaLabel={t('ARIA.GO_TO')}
                      />
                    )
                  }),
                )}
              </div>
            )}

            {isSuccess && fetchedData && hasNextPage && (
              <div tw="w-full flex justify-center pt-12">
                <BrandButton as="button" buttonStyle="default" variant="primary" handlePress={() => handleLoadMore()}>
                  {t('BUTTON.LOAD_MORE')}
                </BrandButton>
              </div>
            )}
          </div>
        </div>
      </Inline>
    </MainLayout>
  )
}

export default NouvellesPage
